import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Building, BedDouble, Bath, DollarSign, Image as ImageIcon, Calendar, CheckCircle, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import * as nsfwjs from 'nsfwjs';
import api from '../../api/axiosConfig';

// === NSFW MODEL (loaded once globally) ===
let nsfwModelPromise = null;

const getNsfwModel = () => {
  if (!nsfwModelPromise) {
    nsfwModelPromise = nsfwjs.load().catch(e => {
      console.warn('NSFW.js failed to load:', e);
      return null;
    });
  }
  return nsfwModelPromise;
};

// PROPERTY IMAGE VALIDATOR (Your ResNet50 Model)
const validatePropertyImage = async (file) => {
  const formData = new FormData();
  formData.append('image', file);
  
  try {
    // Call your backend ML endpoint
    const response = await api.post('/properties/validate-image/', formData, {
      headers: {
        // DO NOT set Content-Type - browser handles FormData boundary automatically
      },
    });
    
    if (!response.ok) throw new Error('Validation failed');
    return await response.json();
    
  } catch (error) {
    console.warn('Property validation unavailable:', error.message);
    return {
      is_real_estate: true,
      confidence: 0.0,
      warning: 'Validation service unavailable - image will be reviewed'
    };
  }
};


// === MAIN COMPONENT ===
export default function Upload() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { t } = useTranslation('Uploaddash');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'rental',
    country: '',
    city: '',
    sector: '',
    price_rwf: '',
    bedrooms: '',
    bathrooms: '',
    available: true,
    hotel_stars: '',
    check_in_time: '',
    check_out_time: '',
  });

  const [photos, setPhotos] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScanning, setIsScanning] = useState(false);

  // SINGLE, CORRECTED NSFW SCANNER
  const scanImageForNSFW = async (file) => {
    const model = await getNsfwModel();
    if (!model) return false;
    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = async () => {
        try {
          const predictions = await model.classify(img);
          URL.revokeObjectURL(img.src);
          const porn = predictions.find(p => p.className === 'Porn');
          const sexy = predictions.find(p => p.className === 'Sexy');
          resolve((porn?.probability > 0.7) || (sexy?.probability > 0.7));
        } catch (e) {
          URL.revokeObjectURL(img.src);
          resolve(false);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        resolve(false);
      };
    });
  };

  // Property Image Validator (ResNet50 Model)
  const scanImageForProperty = async (file) => {
    const result = await validatePropertyImage(file);

    if(!result.is_real_estate && result.confidence > 0.75) {
      return {
        allowed: false,
        reason: "non_property",
        confidence: result.confidence,
        warning: result.warning
      };
    }

    if (result.warning || result.confidence < 0.6) {
      return {
        allowed: true,
        reason: "low_confidence",
        confidence: result.confidence,
        warning: result.warning
      };
    }

    return {
      allowed: true,
      reason: "approved",
      confidence: result.confidence
    };
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePhotoChange = async (e) => {
  const files = Array.from(e.target.files);
  
  // Early exit if too many total
  if (files.length + photos.length > 6) {
    alert(t('upload.alerts.maxPhotos'));
    // Clear input so user can try again
    if (fileInputRef.current) fileInputRef.current.value = '';
    return;
  }

  setIsScanning(true);
  const safePhotos = [];
  const objectUrlsToRevoke = []; // track URLs to clean up if we abort

  try {
    for (const file of files) {
      const isUnsafe = await scanImageForNSFW(file);
      if (isUnsafe) {
        alert(t('upload.alerts.inappropriateImage'));
        objectUrlsToRevoke.forEach(url => URL.revokeObjectURL(url));
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      // Property Validation (Model)
      const propertyCheck = await scanImageForProperty(file);

      if(!propertyCheck.allowed) {
        alert(t('upload.alerts.notPropertyImage', {
          confidence: Math.round(propertyCheck.confidence * 100)
        }));

        objectUrlsToRevoke.forEach(url => URL.revokeObjectURL(url));
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      if (propertyCheck.warning) {
        console.warn("Property validation warning:", propertyCheck.warning);
      }

      const preview = URL.createObjectURL(file);
      objectUrlsToRevoke.push(preview);
      safePhotos.push({file, preview});
    }

    // If we get here, all are safe 
    setPhotos(prev => {
      const newPhotos = [...prev, ...safePhotos];
      if (newPhotos.length > 6) {
        // Revoke extras beyond 6
        newPhotos.slice(6).forEach(p => URL.revokeObjectURL(p.preview));
        return newPhotos.slice(0, 6);
      }
      return newPhotos;
    });

  } catch (err) {
    console.error('Photo processing error:', err);
    objectUrlsToRevoke.forEach(url => URL.revokeObjectURL(url));
    alert(t('upload.alerts.errorProcessingImages'));
  } finally {
    // Clear input so user can select again cleanly
    setIsScanning(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }
};

  const removePhoto = (index) => {
    setPhotos(prev => {
      const newPhotos = [...prev];
      URL.revokeObjectURL(newPhotos[index].preview);
      newPhotos.splice(index, 1);
      return newPhotos;
    });
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = t('upload.validation.title');
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.sector.trim()) newErrors.sector = 'Neighborhood/District is required';
    if (
      !formData.price_rwf ||
      isNaN(formData.price_rwf) ||
      Number(formData.price_rwf) <= 0
    ) {
      newErrors.price_rwf = 'Valid price is required';
    }
    if (photos.length === 0) newErrors.photos = 'At least one photo is required';

    if (formData.type === 'rental') {
      if (
        formData.deposit_rwf &&
        (isNaN(formData.deposit_rwf) || Number(formData.deposit_rwf) < 0)
      ) {
        newErrors.deposit_rwf = 'Valid deposit amount required';
      }
    }

    if (formData.type === 'hotel') {
      const stars = Number(formData.hotel_stars);
      if (!formData.hotel_stars || isNaN(stars) || stars < 1 || stars > 5) {
        newErrors.hotel_stars = 'Hotel stars must be 1-5';
      }
      if (!formData.check_in_time) newErrors.check_in_time = 'Check-in time required';
      if (!formData.check_out_time) newErrors.check_out_time = 'Check-out time required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== '' && value !== null) {
        formDataToSend.append(key, value);
      }
    });
    photos.forEach(photo => {
      formDataToSend.append('images', photo.file);
    });

    // NEW using axios instance
    try {
        await api.post('/properties/', formDataToSend, {
        withCredentials: true, // ensure cookies go with it
        headers: {
          // VERY IMPORTANT: do NOT set Content-Type, axios will handle FormData boundary
        },
      });

      // success
      navigate('/homedash');
    } catch (err) {
      const errorData = err.response?.data || {};
      console.error('Submission error:', errorData);
      alert(
        t('upload.alerts.error', {
          error: errorData.error || errorData.detail || 'Unknown error',
        })
      );
    }
    setIsSubmitting(false);
  };


  const getTypeLabel = (type) => {
    return t(`upload.form.types.${type}`);
  };

  useEffect(() => {
    return () => {
      photos.forEach(photo => {
        URL.revokeObjectURL(photo.preview);
      });
    };
  }, [photos]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/get-csrf/`, { credentials: 'include' }).catch(console.error);
  }, []);

  return (
    <div className="min-h-screen bg-[var(--bg-black)] text-[var(--text-black)] pb-20">
      <header className="sticky top-0 z-10 bg-[var(--bg-black)] border-b px-4 py-3 flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="rounded-full border-[var(--border)] no-underline border-solid w-10 h-10 bg-[var(--bg-black)] transition flex items-center justify-center"
          aria-label={t('upload.header.back')}
        >
          <svg className="w-6 h-6 text-[var(--text-black)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-xl font-bold no-underline">{t('upload.header.title')}</h1>
      </header>
      {/* Form */}
      <div className="max-w-2xl mx-auto p-4 space-y-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold mb-2 text-[var(--text-black)]">{t('upload.form.listingType')}</label>
            <div className="grid grid-cols-3 gap-3">
              {[                
                { id: 'rental', label: t('upload.form.types.rental'), icon: <Home size={20} /> },
                { id: 'property', label: t('upload.form.types.property'), icon: <Building size={20} /> },
                { id: 'hotel', label: t('upload.form.types.hotel'), icon: <BedDouble size={20} /> }
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: opt.id }))}
                  className={`flex flex-col items-center justify-center p-4 rounded-xl border transition ${
                    formData.type === opt.id
                      ? 'border-pink-500 text-pink-400 bg-[var(--bg-black)] border-solid'
                      : 'border-[var(--border)] border-solid text-[var(--text-black)] bg-[var(--bg-black)]'
                  }`}
                >
                  {opt.icon}
                  <span className="text-sm mt-1">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-[var(--text-black)]">{t('upload.form.fields.title.label')}</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              // placeholder={`E.g. ${getTypeLabel(formData.type)} in ${formData.city || 'City'}`}
              placeholder={t('upload.form.fields.title.placeholder', { 
                type: t(`upload.form.types.${formData.type}`),
                city: formData.city || t('upload.form.fields.location.city.placeholder').split(',')[0] 
              })}
              className="w-full px-4 py-2.5 rounded-lg bg-[var(--bg-black)] border border-[var(--border)] border-solid text-[var(--text-black)] focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
            {errors.title && <p className="text-red-400 text-sm mt-1">{errors.title}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-[var(--text-black)]">
              {t('upload.form.fields.description.label')}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              placeholder={t('upload.form.fields.description.placeholder')}
              className="w-full px-4 py-2.5 rounded-lg bg-[var(--bg-black)] border border-[var(--border)] border-solid text-[var(--text-black)] focus:outline-none focus:ring-2 focus:ring-pink-500 placeholder:text-[var(--text-black)] placeholder:font-normal placeholder:opacity-60"
            />
            {errors.description && <p className="text-red-400 text-sm mt-1">{errors.description}</p>}
          </div>

          {/* Location */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2 text-[var(--text-black)]">{t('upload.form.fields.location.country.label')}</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder={t('upload.form.fields.location.country.placeholder')}
                className="w-full px-4 py-2.5 rounded-lg bg-[var(--bg-black)] border border-[var(--border)] border-solid text-[var(--text-black)] focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              {errors.country && <p className="text-red-400 text-sm mt-1">{errors.country}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-[var(--text-black)]">{t('upload.form.fields.location.city.label')}</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder={t('upload.form.fields.title.placeholder', { 
                  type: t(`upload.form.types.${formData.type}`),
                  city: formData.city || t('upload.form.fields.location.city.placeholder').split(',')[0] 
                })}
                className="w-full px-4 py-2.5 rounded-lg bg-[var(--bg-black)] border border-[var(--border)] border-solid text-[var(--text-black)] focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              {errors.city && <p className="text-red-400 text-sm mt-1">{errors.city}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold mb-2 text-[var(--text-black)]">{t('upload.form.fields.location.sector.label')}</label>
              <input
                type="text"
                name="sector"
                value={formData.sector}
                onChange={handleInputChange}
                placeholder={t('upload.form.fields.location.sector.placeholder')}
                className="w-full px-4 py-2.5 rounded-lg bg-[var(--bg-black)] border border-[var(--border)] border-solid text-[var(--text-black)] focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              {errors.sector && <p className="text-red-400 text-sm mt-1">{errors.sector}</p>}
            </div>
          </div>

          {/* Price & Rooms */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-semibold mb-2 text-[var(--text-black)] flex items-center gap-1">
                <DollarSign size={16} /> {t('upload.form.fields.price.label')}
              </label>
              <input
                type="number"
                name="price_rwf"
                value={formData.price_rwf}
                onChange={handleInputChange}
                placeholder={t('upload.form.fields.price.placeholder')}
                min="1"
                className="w-full px-4 py-2.5 rounded-lg bg-[var(--bg-black)] border border-[var(--border)] border-solid text-[var(--text-black)] focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              {errors.price_rwf && <p className="text-red-400 text-sm mt-1">{errors.price_rwf}</p>}
            </div>

            <div>
              <label className="text-sm font-semibold mb-2 text-[var(--text-black)] flex items-center gap-1">
                <BedDouble size={16} /> {t('upload.form.fields.bedrooms.label')}
              </label>
              <input
                type="number"
                name="bedrooms"
                value={formData.bedrooms}
                onChange={handleInputChange}
                min="0"
                className="w-full px-4 py-2.5 rounded-lg bg-[var(--bg-black)] border border-[var(--border)] border-solid text-[var(--text-black)] focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>

            <div>
              <label className="text-sm font-semibold mb-2 text-[var(--text-black)] flex items-center gap-1">
                <Bath size={16} /> {t('upload.form.fields.bathrooms.label')}
              </label>
              <input
                type="number"
                name="bathrooms"
                value={formData.bathrooms}
                onChange={handleInputChange}
                min="0"
                className="w-full px-4 py-2.5 rounded-lg bg-[var(--bg-black)] border border-[var(--border)] border-solid text-[var(--text-black)] focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
          </div>


          {formData.type === 'hotel' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white/5 rounded-xl border border-[var(--border)]">
              <div>
                <label className="text-sm font-semibold mb-2 text-[var(--text-black)] flex items-center gap-1">
                  <Star size={16} /> <label>{t('upload.form.typeSpecific.hotel.stars.label')}</label>
                </label>
                <input
                  type="number"
                  name="hotel_stars"
                  value={formData.hotel_stars}
                  onChange={handleInputChange}
                  min="1"
                  max="5"
                  className="w-full px-4 py-2.5 rounded-lg bg-[var(--bg-black)] border border-[var(--border)] border-solid text-[var(--text-black)] focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
                {errors.hotel_stars && <p className="text-red-400 text-sm mt-1">{errors.hotel_stars}</p>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-sm font-semibold mb-2 text-[var(--text-black)]">{t('upload.form.typeSpecific.hotel.checkIn.label')}</label>
                  <input
                    type="time"
                    name="check_in_time"
                    value={formData.check_in_time}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 rounded-lg bg-[var(--bg-black)] border border-[var(--border)] border-solid text-[var(--text-black)] focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                  {errors.check_in_time && <p className="text-red-400 text-sm mt-1">{errors.check_in_time}</p>}
                </div>
                <div>
                  <label className="text-sm font-semibold mb-2 text-[var(--text-black)]">{t('upload.form.typeSpecific.hotel.checkOut.label')}</label>
                  <input
                    type="time"
                    name="check_out_time"
                    value={formData.check_out_time}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 rounded-lg bg-[var(--bg-black)] border border-[var(--border)] border-solid text-[var(--text-black)] focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                  {errors.check_out_time && <p className="text-red-400 text-sm mt-1">{errors.check_out_time}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Availability */}
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-[var(--border)]">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${formData.available ? 'bg-green-500/20' : 'bg-gray-500/20'}`}>
                <Calendar size={20} className={formData.available ? 'text-green-400' : 'text-gray-400'} />
              </div>
              <div>
                <p className="font-medium text-[var(--text-black)]">
                  {t('upload.form.availability.label', {
                    type: getTypeLabel(formData.type).toLowerCase()
                  })}
                </p>
              </div>
            </div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="available"
                checked={formData.available}
                onChange={handleInputChange}
                className="w-5 h-5 rounded border-2 border-[var(--border)] bg-[var(--bg-black)] border-solid checked:bg-pink-500 checked:border-pink-500 focus:outline-none focus:ring-2 focus:ring-pink-500 cursor-pointer"
              />
            </label>
          </div>

          {/* Photos */}
          <div>
            <label className="text-sm font-semibold mb-2 text-[var(--text-black)] flex items-center gap-1">
              <ImageIcon size={16} /> {t('upload.form.photos.label')}
            </label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${
                errors.photos ? 'border-red-500' : 'border-[var(--border)] hover:border-pink-500'
              }`}
            >
              <ImageIcon size={32} className="mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-400">
                {photos.length > 0
                  ? t('upload.form.photos.uploadMore', { current: photos.length })
                  : t('upload.form.photos.uploadText')}
              </p>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoChange}
                accept="image/*"
                multiple
                className="hidden"
              />
            </div>
            {errors.photos && <p className="text-red-400 text-sm mt-1">{errors.photos}</p>}

            {/* Photo Preview */}
            {photos.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-2">
                {photos.map((photo, index) => (
                  <div key={index} className="relative group">
                   <img
                      src={photo.preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute -top-2 -right-2 border-none bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || isScanning || photos.length === 0}
            className="w-full py-3.5 bg-pink-500 border border-none text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-pink-600 transition disabled:opacity-60"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('upload.form.submit.posting')}
              </>
            ) : (
              <>
                <CheckCircle size={20} />
                {t('upload.form.submit.post')}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}